import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, Sparkles, RefreshCw } from "lucide-react";
import ImageUploadField from "../../components/ImageUploadField";
import MediaManager from "../../components/MediaManager";

const EMPTY = {
  name: "", category: "Diamond", carat: "", weight_unit: "ct",
  dimensions: "", origin: "", treatment: "None / Untreated",
  color: "", clarity: "", cut: "", certification: "GIA",
  reserve_price: "", image_url: "", description: "", vip_only: false,
  images: [], video: null, variations: [], marketplace_links: [],
  // Shipping attributes — defaults match a typical loose gemstone:
  // physical=true, weight_g≈3 g, dimensions populated separately.
  physical: true, weight_g: "", length_mm: "", width_mm: "", height_mm: "",
};

const EMPTY_VARIATION = {
  id: "", size: "", color: "", carat: "", mm: "", price: "", stock: "", image_url: "", sku: "",
};

// External marketplaces the storefront can deep-link to. Keep this in sync
// with the backend `MARKETPLACE_PLATFORMS` Literal.
const MARKETPLACE_PLATFORMS = [
  { key: "shopee", label: "Shopee" },
  { key: "tokopedia", label: "Tokopedia" },
  { key: "tiktok_shop", label: "TikTok Shop" },
];

const genVarId = () => (typeof crypto !== "undefined" && crypto.randomUUID
  ? crypto.randomUUID().replace(/-/g, "").slice(0, 12)
  : Math.random().toString(36).slice(2, 14));

const SEED_CATS = ["Diamond", "Sapphire", "Ruby", "Emerald", "Rare"];
const WEIGHT_UNITS = [
  { v: "ct", l: "Carat (ct)" },
  { v: "g", l: "Gram (g)" },
  { v: "kg", l: "Kilogram (kg)" },
  { v: "pc", l: "Piece (pc)" },
];
const SEED_LABS = ["GIA", "Gübelin", "SSEF", "AGL", "GRS", "AIGS", "Lotus", "GCAL", "IGI", "OPIKJEWELS Bureau"];

const InventoryManager = () => {
  const [gems, setGems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState("All");
  // AI description generator state — true while a request is in flight.
  const [aiBusy, setAiBusy] = useState(false);
  const [aiZodiac, setAiZodiac] = useState(true);

  const load = useCallback(() => api.get("/gemstones").then(({ data }) => setGems(data)), []);
  useEffect(() => { load(); }, [load]);

  /**
   * Compose the field block we send to the AI from whatever the admin has
   * filled into the editor. Empty values are stripped server-side. Returns
   * `null` (and surfaces a toast) when there's literally nothing to work with.
   */
  const generateAIDescription = async () => {
    const carat = parseFloat(form.carat);
    const weightDisplay = carat ? `${carat} ${form.weight_unit || "ct"}` : "";
    const payload = {
      name: form.name?.trim() || undefined,
      category: form.category?.trim() || undefined,
      gem_type: form.category?.trim() || undefined,  // category doubles as gem type for raw stones
      weight: weightDisplay || undefined,
      dimensions: form.dimensions?.trim() || undefined,
      color: form.color?.trim() || undefined,
      clarity: form.clarity?.trim() || undefined,
      origin: form.origin?.trim() || undefined,
      treatment: form.treatment?.trim() || undefined,
      cut: form.cut?.trim() || undefined,
      certification: form.certification?.trim() || undefined,
      price_range: form.reserve_price ? `from Rp ${parseFloat(form.reserve_price).toLocaleString("id-ID")} IDR` : undefined,
      stock_status: undefined,
      include_zodiac: aiZodiac,
    };
    const hasAnyField = Object.entries(payload).some(([k, v]) => k !== "include_zodiac" && v);
    if (!hasAnyField) {
      toast.error("Add a product name or at least one detail before generating.");
      return;
    }
    setAiBusy(true);
    try {
      const { data } = await api.post("/admin/ai/product-description", payload);
      if (data?.description) {
        setForm((f) => ({ ...f, description: data.description }));
        toast.success("AI description generated.");
      } else {
        toast.error("AI returned no description.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed.");
    } finally {
      setAiBusy(false);
    }
  };

  // Dynamic category list = seed + any custom categories already used in inventory
  const allCategories = useMemo(() => {
    const set = new Set(SEED_CATS);
    gems.forEach((g) => g.category && set.add(g.category));
    return Array.from(set);
  }, [gems]);

  const startCreate = () => { setEditing("new"); setForm(EMPTY); };
  const startEdit = (g) => {
    setEditing(g.id);
    setForm({
      ...g,
      carat: g.weight_value ?? g.carat ?? "",
      weight_unit: g.weight_unit || "ct",
      dimensions: g.dimensions || "",
      images: Array.isArray(g.images) ? g.images : [],
      video: g.video || null,
      variations: Array.isArray(g.variations)
        ? g.variations.map((v) => ({
            ...EMPTY_VARIATION,
            ...v,
            id: v.id || genVarId(),
            carat: v.carat ?? "",
            price: v.price ?? "",
            stock: v.stock ?? "",
          }))
        : [],
      marketplace_links: Array.isArray(g.marketplace_links) ? g.marketplace_links : [],
      physical: g.physical !== false,
      weight_g: g.weight_g ?? "",
      length_mm: g.length_mm ?? "",
      width_mm: g.width_mm ?? "",
      height_mm: g.height_mm ?? "",
      gallery: undefined, available: undefined, currency: undefined,
    });
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    // Keep legacy `image_url` and `gallery` in sync with the new structured media model
    const imgList = Array.isArray(form.images) ? form.images : [];
    const coverUrl = imgList[0]?.url || form.image_url || "";
    const galleryUrls = imgList.slice(1).map((x) => x.url).filter(Boolean);

    if (!coverUrl) {
      toast.error("At least 1 product image is required.");
      return;
    }

    // Serialize variations — drop empty rows, coerce numeric fields, strip blanks
    const cleanVariations = (form.variations || [])
      .map((v) => ({
        id: v.id || genVarId(),
        size: (v.size || "").trim() || null,
        color: (v.color || "").trim() || null,
        carat: v.carat === "" || v.carat == null ? null : parseFloat(v.carat),
        mm: (v.mm || "").trim() || null,
        price: v.price === "" || v.price == null ? null : parseFloat(v.price),
        stock: v.stock === "" || v.stock == null ? null : parseInt(v.stock, 10),
        image_url: (v.image_url || "").trim() || null,
        sku: (v.sku || "").trim() || null,
      }))
      // A variation must have at least one defining attribute OR a price override
      .filter((v) => v.size || v.color || v.carat || v.mm || v.price || v.sku);

    // Serialize marketplace links — drop empty URLs entirely
    const cleanMarketplaceLinks = (form.marketplace_links || [])
      .map((m) => ({
        platform: m.platform,
        url: (m.url || "").trim(),
        enabled: m.enabled !== false,
      }))
      .filter((m) => m.platform && m.url);

    const payload = {
      ...form,
      carat: parseFloat(form.carat) || 0,
      reserve_price: parseFloat(form.reserve_price) || 0,
      // OPIKJEWELS base-currency policy: every new product is stored in IDR.
      // Per-product overrides only happen on imports from the inventory portal,
      // which preserves the synced currency. Manual admin entries are IDR-only.
      currency: form.source === "inventory_portal" ? (form.currency || "IDR") : "IDR",
      vip_only: !!form.vip_only,
      category: (form.category || "").trim() || "Other",
      dimensions: form.dimensions || null,
      image_url: coverUrl,
      gallery: galleryUrls,
      images: imgList,
      video: form.video || null,
      variations: cleanVariations,
      marketplace_links: cleanMarketplaceLinks,
      // Shipping attributes — null when empty so Pydantic Optional kicks in.
      physical: !!form.physical,
      weight_g: form.weight_g === "" ? null : parseFloat(form.weight_g) || null,
      length_mm: form.length_mm === "" ? null : parseFloat(form.length_mm) || null,
      width_mm: form.width_mm === "" ? null : parseFloat(form.width_mm) || null,
      height_mm: form.height_mm === "" ? null : parseFloat(form.height_mm) || null,
    };
    try {
      if (editing === "new") {
        await api.post("/gemstones", payload);
        toast.success("Gemstone created.");
      } else {
        await api.put(`/gemstones/${editing}`, payload);
        toast.success("Gemstone updated.");
      }
      cancel(); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This is irreversible.`)) return;
    try { await api.delete(`/gemstones/${id}`); toast.success("Deleted."); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const filtered = filter === "All" ? gems : gems.filter((g) => g.category === filter);

  return (
    <div data-testid="inventory-manager" className="pb-32">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div className="flex flex-wrap gap-2">
          {["All", ...allCategories].map((c) => (
            <button key={c} data-testid={`inv-filter-${c.toLowerCase().replace(/\s+/g, '-')}`} onClick={() => setFilter(c)}
              className={`font-accent text-[10px] tracking-[0.24em] uppercase px-4 py-2 border transition-colors ${filter === c ? "border-gold text-gold" : "border-white/15 text-white/50 hover:text-white"}`}>
              {c}
            </button>
          ))}
        </div>
        <button data-testid="inv-new-btn" onClick={startCreate} className="bg-gold text-black font-accent uppercase tracking-[0.24em] text-xs px-6 py-3 hover:bg-ivory flex items-center gap-2">
          <Plus size={14} /> New Gemstone
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-white/40 font-accent text-[10px] tracking-[0.28em] uppercase border-b border-white/10">
            <tr><th className="text-left py-4">Name</th><th className="text-left">Cat</th><th className="text-left">Weight</th><th className="text-left">Dimensions</th><th className="text-left">Origin</th><th className="text-left">Cert</th><th className="text-left">Reserve</th><th className="text-left">VIP</th><th></th></tr>
          </thead>
          <tbody>
            {filtered.map((g) => (
              <tr key={g.id} data-testid={`inv-row-${g.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="py-4 font-heading text-lg">
                  {g.name}
                  {g.source === "inventory_portal" && (
                    <span data-testid={`inv-row-synced-${g.id}`} className="ml-2 inline-block align-middle text-[9px] font-accent tracking-[0.2em] uppercase text-gold border border-gold/40 px-1.5 py-0.5">SYNCED</span>
                  )}
                </td>
                <td>{g.category}</td>
                <td>{g.carat} {g.weight_unit || "ct"}</td>
                <td className="text-white/50">{g.dimensions || "—"}</td>
                <td>{g.origin}</td><td>{g.certification}</td>
                <td className="text-ivory">Rp {Intl.NumberFormat("id-ID").format(g.reserve_price)}</td>
                <td>{g.vip_only ? <span className="text-gold">Yes</span> : "—"}</td>
                <td className="text-right">
                  <button data-testid={`inv-edit-${g.id}`} onClick={() => startEdit(g)} className="text-white/60 hover:text-gold p-2"><Pencil size={14} /></button>
                  <button data-testid={`inv-delete-${g.id}`} onClick={() => remove(g.id, g.name)} className="text-white/60 hover:text-red-400 p-2"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="text-white/40 py-8 text-center">No gemstones in this category.</div>}
      </div>

      {editing && (
        <div data-testid="inv-modal" className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm overflow-y-auto" onClick={cancel}>
          <div className="max-w-4xl mx-auto my-12 bg-noir border border-gold/40 p-8 md:p-10" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-8">
              <h2 className="font-heading text-3xl">{editing === "new" ? "New Gemstone" : "Edit Gemstone"}</h2>
              <button onClick={cancel} className="text-white/60 hover:text-white"><X size={20} /></button>
            </div>
            {form.source === "inventory_portal" && (
              <div data-testid="inv-synced-banner" className="border border-gold/40 bg-onyx px-5 py-4 mb-6 flex items-start gap-3">
                <span className="text-gold mt-1">●</span>
                <div className="text-sm">
                  <div className="text-gold font-accent tracking-[0.22em] uppercase text-[11px] mb-1">Synced from Inventory Portal</div>
                  <div className="text-white/60">
                    Inventory ID <code className="text-ivory">{form.inventory_id}</code>
                    {form.stock !== null && form.stock !== undefined ? <> · Stock <span className="text-ivory">{form.stock}</span></> : null}
                    {form.last_synced_at ? <> · Last sync <span className="text-white/70">{new Date(form.last_synced_at).toLocaleString()}</span></> : null}
                  </div>
                  <div className="text-white/40 text-xs mt-2">
                    SKU, stock and reserve price are locked — they always sync from the portal. Description, images, video and layout remain fully editable.
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name" v={form.name} on={(v) => setForm({...form, name: v})} testid="inv-f-name" />
              <ComboField label="Category" v={form.category} on={(v) => setForm({...form, category: v})}
                options={allCategories} placeholder="Diamond, Sapphire, Tourmaline…" testid="inv-f-cat" listId="cat-options" />
              <Field label="Weight" v={form.carat} on={(v) => setForm({...form, carat: v})} type="number" step="0.01" testid="inv-f-carat" />
              <Field label="Weight Unit" v={form.weight_unit} on={(v) => setForm({...form, weight_unit: v})}
                options={WEIGHT_UNITS.map((u) => ({ value: u.v, label: u.l }))} testid="inv-f-unit" />
              <Field label="Dimensions (e.g. 12 × 12 × 6 mm)" v={form.dimensions} on={(v) => setForm({...form, dimensions: v})} testid="inv-f-dim" />
              <Field label="Origin" v={form.origin} on={(v) => setForm({...form, origin: v})} testid="inv-f-origin" />
              <Field label="Treatment" v={form.treatment} on={(v) => setForm({...form, treatment: v})} testid="inv-f-treat" />
              <Field label="Color" v={form.color} on={(v) => setForm({...form, color: v})} testid="inv-f-color" />
              <Field label="Clarity" v={form.clarity} on={(v) => setForm({...form, clarity: v})} testid="inv-f-clarity" />
              <Field label="Cut" v={form.cut} on={(v) => setForm({...form, cut: v})} testid="inv-f-cut" />
              <ComboField label="Certification Lab" v={form.certification} on={(v) => setForm({...form, certification: v})}
                options={SEED_LABS} placeholder="GIA, Gübelin, custom lab name…" testid="inv-f-cert" listId="lab-options" />
              <Field label="Reserve Price (Rp · IDR)" v={form.reserve_price} on={(v) => setForm({...form, reserve_price: v})} type="number" testid="inv-f-reserve" readOnly={form.source === "inventory_portal"} />
            </div>

            <div className="mt-8">
              <label className="text-white/50 text-xs tracking-[0.22em] font-accent uppercase mb-3 block">Media — Images & Video</label>
              <MediaManager
                images={form.images || []}
                video={form.video || null}
                onChange={({ images, video }) => setForm({ ...form, images, video })}
                folder="opikjewels/gemstones"
              />
            </div>

            {/* Variations editor — optional size / color / carat / mm rows with price + stock overrides */}
            <div className="mt-10" data-testid="inv-variations-block">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <label className="text-white/50 text-xs tracking-[0.22em] font-accent uppercase">Variations</label>
                  <p className="text-white/40 text-xs mt-1 font-light">Optional — leave empty if this stone has no size/color/carat/mm options. Empty cells inherit the base product values; the Price column overrides Reserve Price.</p>
                </div>
                <button
                  type="button"
                  data-testid="inv-add-variation"
                  onClick={() => setForm({ ...form, variations: [...(form.variations || []), { ...EMPTY_VARIATION, id: genVarId() }] })}
                  className="border border-gold text-gold font-accent uppercase tracking-[0.24em] text-[10px] px-4 py-2 hover:bg-gold hover:text-black flex items-center gap-2"
                >
                  <Plus size={12} /> Add Variation
                </button>
              </div>

              {(form.variations || []).length > 0 && (
                <div className="overflow-x-auto border border-white/10">
                  <table className="w-full text-sm">
                    <thead className="text-white/40 font-accent text-[10px] tracking-[0.22em] uppercase bg-white/[0.02]">
                      <tr>
                        <th className="text-left px-3 py-2">Size</th>
                        <th className="text-left px-3 py-2">Color</th>
                        <th className="text-left px-3 py-2">Carat</th>
                        <th className="text-left px-3 py-2">mm</th>
                        <th className="text-left px-3 py-2">Price (Rp · IDR)</th>
                        <th className="text-left px-3 py-2">Stock</th>
                        <th className="text-left px-3 py-2">SKU</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(form.variations || []).map((v, i) => {
                        const patch = (key, val) => {
                          const next = [...form.variations];
                          next[i] = { ...next[i], [key]: val };
                          setForm({ ...form, variations: next });
                        };
                        return (
                          <tr key={v.id || i} className="border-t border-white/5" data-testid={`inv-variation-row-${i}`}>
                            <td className="px-2 py-1"><input data-testid={`inv-var-size-${i}`} value={v.size || ""} onChange={(e) => patch("size", e.target.value)} placeholder="e.g. 6" className="w-full bg-transparent border-b border-white/15 px-2 py-2 text-white focus:outline-none focus:border-gold" /></td>
                            <td className="px-2 py-1"><input data-testid={`inv-var-color-${i}`} value={v.color || ""} onChange={(e) => patch("color", e.target.value)} placeholder="Pigeon Blood" className="w-full bg-transparent border-b border-white/15 px-2 py-2 text-white focus:outline-none focus:border-gold" /></td>
                            <td className="px-2 py-1"><input data-testid={`inv-var-carat-${i}`} type="number" step="0.01" value={v.carat || ""} onChange={(e) => patch("carat", e.target.value)} placeholder="1.05" className="w-24 bg-transparent border-b border-white/15 px-2 py-2 text-white focus:outline-none focus:border-gold" /></td>
                            <td className="px-2 py-1"><input data-testid={`inv-var-mm-${i}`} value={v.mm || ""} onChange={(e) => patch("mm", e.target.value)} placeholder="6×8 mm" className="w-full bg-transparent border-b border-white/15 px-2 py-2 text-white focus:outline-none focus:border-gold" /></td>
                            <td className="px-2 py-1"><input data-testid={`inv-var-price-${i}`} type="number" step="1" value={v.price || ""} onChange={(e) => patch("price", e.target.value)} placeholder="—" className="w-28 bg-transparent border-b border-white/15 px-2 py-2 text-white focus:outline-none focus:border-gold" /></td>
                            <td className="px-2 py-1"><input data-testid={`inv-var-stock-${i}`} type="number" step="1" value={v.stock || ""} onChange={(e) => patch("stock", e.target.value)} placeholder="—" className="w-20 bg-transparent border-b border-white/15 px-2 py-2 text-white focus:outline-none focus:border-gold" /></td>
                            <td className="px-2 py-1"><input data-testid={`inv-var-sku-${i}`} value={v.sku || ""} onChange={(e) => patch("sku", e.target.value)} placeholder="optional" className="w-28 bg-transparent border-b border-white/15 px-2 py-2 text-white focus:outline-none focus:border-gold" /></td>
                            <td className="px-1 py-1 text-right">
                              <button
                                type="button"
                                data-testid={`inv-var-delete-${i}`}
                                onClick={() => setForm({ ...form, variations: form.variations.filter((_, j) => j !== i) })}
                                className="text-white/50 hover:text-red-400 p-2"
                                title="Remove variation"
                              >
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Marketplace links editor — Shopee / Tokopedia / TikTok Shop.
                One row per supported platform; an empty URL or `enabled=false`
                hides that button on the storefront automatically. */}
            <div className="mt-10" data-testid="inv-marketplace-block">
              <div className="mb-4">
                <label className="text-white/50 text-xs tracking-[0.22em] font-accent uppercase">Marketplace Checkout Links</label>
                <p className="text-white/40 text-xs mt-1 font-light">
                  Paste the product URL on each marketplace. Toggle a row off to temporarily hide it from the storefront. Empty URLs are auto-hidden.
                </p>
              </div>
              <div className="border border-white/10 divide-y divide-white/10">
                {MARKETPLACE_PLATFORMS.map((p) => {
                  const idx = (form.marketplace_links || []).findIndex((m) => m.platform === p.key);
                  const row = idx >= 0 ? form.marketplace_links[idx] : { platform: p.key, url: "", enabled: true };
                  const patch = (key, val) => {
                    const next = [...(form.marketplace_links || [])];
                    const existing = next.findIndex((m) => m.platform === p.key);
                    if (existing >= 0) {
                      next[existing] = { ...next[existing], [key]: val };
                    } else {
                      next.push({ platform: p.key, url: "", enabled: true, [key]: val });
                    }
                    setForm({ ...form, marketplace_links: next });
                  };
                  return (
                    <div key={p.key} data-testid={`inv-mp-row-${p.key}`} className="flex flex-wrap items-center gap-3 px-4 py-3">
                      <div className="w-24 font-accent text-[10px] tracking-[0.22em] uppercase text-gold shrink-0">{p.label}</div>
                      <input
                        data-testid={`inv-mp-url-${p.key}`}
                        value={row.url || ""}
                        onChange={(e) => patch("url", e.target.value)}
                        placeholder={`https://${p.key === "tiktok_shop" ? "shop.tiktok.com" : `${p.key}.co.id`}/...`}
                        className="flex-1 min-w-[200px] bg-transparent border-b border-white/15 px-2 py-2 text-white focus:outline-none focus:border-gold"
                      />
                      <label className="flex items-center gap-2 cursor-pointer text-white/70 text-xs">
                        <input
                          data-testid={`inv-mp-enabled-${p.key}`}
                          type="checkbox"
                          checked={row.enabled !== false}
                          onChange={(e) => patch("enabled", e.target.checked)}
                          className="accent-gold w-4 h-4"
                        />
                        <span className="font-accent tracking-[0.18em] uppercase text-[10px]">Enabled</span>
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Shipping attributes — physical / weight / dimensions */}
            <div className="mt-10" data-testid="inv-shipping-block">
              <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <label className="text-white/50 text-xs tracking-[0.22em] font-accent uppercase">Shipping Attributes</label>
                  <p className="text-white/40 text-xs mt-1 font-light">
                    Toggle off only for digital items. Weight + dimensions are sent to couriers and used for accurate quotes.
                  </p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer text-white/70 text-xs">
                  <input
                    data-testid="inv-physical"
                    type="checkbox"
                    checked={!!form.physical}
                    onChange={(e) => setForm({ ...form, physical: e.target.checked })}
                    className="accent-gold w-4 h-4"
                  />
                  <span className="font-accent tracking-[0.18em] uppercase text-[10px]">Physical product</span>
                </label>
              </div>
              {form.physical && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 border border-white/10 px-4 py-4">
                  <div>
                    <label className="text-white/45 text-[10px] tracking-[0.18em] font-accent uppercase mb-1 block">Weight (g)</label>
                    <input
                      data-testid="inv-weight-g"
                      type="number" step="0.01" min="0" placeholder="e.g. 5.2"
                      value={form.weight_g}
                      onChange={(e) => setForm({ ...form, weight_g: e.target.value })}
                      className="w-full bg-transparent border-b border-white/15 px-2 py-2 text-white text-sm focus:outline-none focus:border-gold"
                    />
                  </div>
                  <div>
                    <label className="text-white/45 text-[10px] tracking-[0.18em] font-accent uppercase mb-1 block">Length (mm)</label>
                    <input
                      data-testid="inv-length-mm"
                      type="number" step="0.1" min="0" placeholder="e.g. 80"
                      value={form.length_mm}
                      onChange={(e) => setForm({ ...form, length_mm: e.target.value })}
                      className="w-full bg-transparent border-b border-white/15 px-2 py-2 text-white text-sm focus:outline-none focus:border-gold"
                    />
                  </div>
                  <div>
                    <label className="text-white/45 text-[10px] tracking-[0.18em] font-accent uppercase mb-1 block">Width (mm)</label>
                    <input
                      data-testid="inv-width-mm"
                      type="number" step="0.1" min="0" placeholder="e.g. 60"
                      value={form.width_mm}
                      onChange={(e) => setForm({ ...form, width_mm: e.target.value })}
                      className="w-full bg-transparent border-b border-white/15 px-2 py-2 text-white text-sm focus:outline-none focus:border-gold"
                    />
                  </div>
                  <div>
                    <label className="text-white/45 text-[10px] tracking-[0.18em] font-accent uppercase mb-1 block">Height (mm)</label>
                    <input
                      data-testid="inv-height-mm"
                      type="number" step="0.1" min="0" placeholder="e.g. 30"
                      value={form.height_mm}
                      onChange={(e) => setForm({ ...form, height_mm: e.target.value })}
                      className="w-full bg-transparent border-b border-white/15 px-2 py-2 text-white text-sm focus:outline-none focus:border-gold"
                    />
                  </div>
                </div>
              )}
            </div>

            <details className="mt-4">
              <summary className="text-white/40 text-xs font-accent tracking-[0.22em] uppercase cursor-pointer hover:text-white">
                Advanced — paste a single image URL (legacy)
              </summary>
              <div className="mt-3">
                <ImageUploadField
                  label="Legacy Image URL"
                  value={form.image_url}
                  onChange={(v) => setForm({...form, image_url: v})}
                  testid="inv-f-img"
                  helper="Only used when no Cloudinary images are uploaded above. Will be replaced by the cover image on save."
                />
              </div>
            </details>
            <div className="mt-4">
              <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                <label className="text-white/50 text-xs tracking-[0.18em] font-accent uppercase">Description</label>
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="flex items-center gap-2 cursor-pointer text-white/55 text-[10px] tracking-[0.18em] uppercase font-accent">
                    <input
                      type="checkbox"
                      data-testid="inv-ai-zodiac"
                      checked={aiZodiac}
                      onChange={(e) => setAiZodiac(e.target.checked)}
                      className="accent-gold w-3.5 h-3.5"
                    />
                    Zodiac · Birthstone
                  </label>
                  <button
                    type="button"
                    data-testid="inv-ai-generate"
                    onClick={generateAIDescription}
                    disabled={aiBusy}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gold/50 text-gold hover:bg-gold hover:text-noir transition-colors font-accent text-[10px] tracking-[0.22em] uppercase disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {aiBusy
                      ? <><RefreshCw size={12} className="animate-spin" /> Generating</>
                      : form.description
                        ? <><RefreshCw size={12} /> Regenerate</>
                        : <><Sparkles size={12} /> AI Description</>
                    }
                  </button>
                </div>
              </div>
              <textarea data-testid="inv-f-desc" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={8}
                placeholder="Click ✨ AI Description to auto-generate from the fields above, or write your own."
                className="w-full bg-transparent border border-white/20 px-3 py-3 text-white focus:outline-none focus:border-gold leading-relaxed" />
              <p className="text-white/35 text-[10px] tracking-[0.18em] uppercase font-accent mt-2">
                AI uses the fields above. You can edit the generated text freely.
              </p>
            </div>
            <label className="flex items-center gap-3 mt-4 cursor-pointer">
              <input data-testid="inv-f-vip" type="checkbox" checked={!!form.vip_only} onChange={(e) => setForm({...form, vip_only: e.target.checked})} className="accent-gold w-4 h-4" />
              <span className="text-white/70 text-sm">VIP-only (hidden from public collection)</span>
            </label>
            <div className="flex gap-3 mt-8">
              <button data-testid="inv-save-btn" onClick={save} className="bg-gold text-black font-accent uppercase tracking-[0.24em] text-xs px-7 py-3 hover:bg-ivory">
                {editing === "new" ? "Create" : "Save Changes"}
              </button>
              <button onClick={cancel} className="border border-white/30 text-white font-accent uppercase tracking-[0.24em] text-xs px-7 py-3 hover:border-white">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, v, on, type = "text", step, options, testid, readOnly = false }) => (
  <div>
    <label className="text-white/50 text-xs tracking-[0.18em] font-accent uppercase mb-2 block">
      {label}{readOnly ? <span className="text-gold/70 normal-case tracking-normal ml-2 text-[10px]">· synced</span> : null}
    </label>
    {options ? (
      <select data-testid={testid} value={v || ""} onChange={readOnly ? undefined : (e) => on(e.target.value)} disabled={readOnly}
        className={`w-full bg-transparent border px-3 py-3 text-white focus:outline-none ${readOnly ? "border-white/10 text-white/60 cursor-not-allowed" : "border-white/20 focus:border-gold"}`}>
        {options.map((o) => {
          const value = typeof o === "string" ? o : o.value;
          const label = typeof o === "string" ? o : o.label;
          return <option key={value} value={value} className="bg-noir">{label}</option>;
        })}
      </select>
    ) : (
      <input data-testid={testid} type={type} step={step} value={v || ""} readOnly={readOnly}
        onChange={readOnly ? undefined : (e) => on(e.target.value)}
        className={`w-full bg-transparent border px-3 py-3 text-white focus:outline-none ${readOnly ? "border-white/10 text-white/60 cursor-not-allowed" : "border-white/20 focus:border-gold"}`} />
    )}
  </div>
);

// Combobox: input + datalist for free-text with autocomplete suggestions
const ComboField = ({ label, v, on, options, placeholder, testid, listId }) => (
  <div>
    <label className="text-white/50 text-xs tracking-[0.18em] font-accent uppercase mb-2 block">{label}</label>
    <input data-testid={testid} list={listId} value={v || ""} onChange={(e) => on(e.target.value)} placeholder={placeholder}
      className="w-full bg-transparent border border-white/20 px-3 py-3 text-white focus:outline-none focus:border-gold" />
    <datalist id={listId}>
      {options.map((o) => <option key={o} value={o} />)}
    </datalist>
  </div>
);

export default InventoryManager;
